import styled from 'styled-components'
import breakpoints from '../../../../config/Breakpoints'
import CardTitle from '../../../../common/styles/CardTitle'
import LoadingBar from '../../../../common/LoadingBar'
import useLanguageReadingSpeed from '../hooks/useLanguageReadingSpeed'
import {BookLanguage, getBookLanguageDisplayName, getBookLanguageFlag} from '../../BookType'
import {formatSpeedDifference, getNativePagesPerHour, getSpeedTone, SpeedTone} from '../responses/LanguageReadingSpeedResponse'

const getLanguageName = (language: BookLanguage): string => language === 'UNKNOWN' ? '알 수 없음' : getBookLanguageDisplayName(language)

const BookStatisticsLanguageReadingSpeedCard = () => {
	const [data, isLoading] = useLanguageReadingSpeed()

	// 원어민 평균(연구 값)이 있는 언어만, 읽은 시간 많은 순 (서버 정렬 그대로)
	const rows = (data?.languages ?? []).filter(language => language.native != null)

	return (
		<Container>
			<CardTitle icon={null} mb={0} title={'언어별 읽기 속도'} url={''} textSize={5}/>

			{isLoading ? (
				<Table>
					<tbody>
					{[0, 1].map(i => (
						<tr key={i}>
							<th className={'col-6'}>
								<LoadingBar size={3}/>
							</th>
							<td className={'col-6'}>
								<LoadingValues>
									<LoadingBar size={4}/>
									<LoadingBar size={3}/>
								</LoadingValues>
							</td>
						</tr>
					))}
					</tbody>
				</Table>
			) :  rows.length === 0 ? (
				<EmptyText>페이지와 시간이 함께 기록된 독서 활동이 쌓이면 보여드릴게요</EmptyText>
			) : (
				<>
					<Table>
						<tbody>
						{rows.map(row => {
							const native = row.native!
							return (
								<tr key={row.language}>
									<th className={'col-6 h5'} style={{color: 'var(--text-color)'}}>
										<LanguageFlag>{getBookLanguageFlag(row.language)}</LanguageFlag>
										{getLanguageName(row.language)}
									</th>
									<td className={'col-6'} style={{color: 'var(--text-color)'}}>
										<ValueRow>
											<SpeedValues>
												<MainValue>시간당 {Math.round(row.pagesPerHour)}쪽</MainValue>
												<NativeValue>원어민 평균 시간당 {Math.round(getNativePagesPerHour(native))}쪽</NativeValue>
											</SpeedValues>
											<DiffValue $tone={getSpeedTone(native.differencePercent)}>{formatSpeedDifference(native.differencePercent)}</DiffValue>
										</ValueRow>
									</td>
								</tr>
							)
						})}
						</tbody>
					</Table>
				</>
			)}
		</Container>
	)
}

const Container = styled.div`
	height: 100%;
	min-height: 650px;
	background-color: var(--card-bg-color);
	border-radius: 16px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
	padding: 25px;

	@media (max-width: ${breakpoints.md}) {
		height: auto;
		min-height: 0;
	}
`

const EmptyText = styled.div`
	padding: 40px 0;
	text-align: center;
	color: var(--sub-text-color);
`

const Table = styled.table`
	margin: 0;
	width: 100%;

	tbody {
		background-color: transparent !important;
	}

	th, td {
		border-bottom: 1px solid var(--hover-bg-color);
		padding: 10px 8px;
		vertical-align: middle;
	}
`

const LanguageFlag = styled.span.attrs({
	className: 'me-3',
})`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	font-size: 24px;
	line-height: 1;
`

const LoadingValues = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 6px;
`

// 내 속도와 원어민 평균은 오른쪽 끝을 맞추고, 배수는 두 줄 가운데에
const ValueRow = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
`

const SpeedValues = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
`

const MainValue = styled.span`
	font-size: 16px;
	font-weight: 600;
`

const DiffValue = styled.span<{ $tone: SpeedTone }>`
	font-size: 14px;
	font-weight: 600;
	min-width: 48px;
	text-align: right;
	color: ${props => props.$tone === 'faster' ? 'var(--primary-color)' : props.$tone === 'slower' ? '#e5534b' : 'var(--sub-text-color)'};
`

const NativeValue = styled.div`
	font-size: 12px;
	color: var(--sub-text-color);
	text-align: right;
`

export default BookStatisticsLanguageReadingSpeedCard
